import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import { FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import headshot from "../assets/images/headshot.webp";

const NAV_LINKS = [
  { label: "About", to: "/about", dropdown: [{ label: "In the News", to: "/media" }] },
  { label: "Consulting", to: "/consulting" },
  { label: "Work", to: "/work" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const close = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
  };

  return (
    <NavContainer>
      <LeftSide>
        <Avatar src={headshot} alt="Jim Montour" />
        <Logo to="/" onClick={close}>Jim Montour</Logo>
      </LeftSide>

      <MenuToggle
        onClick={() => setMenuOpen((prev) => !prev)}
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
      >
        {menuOpen ? <FiX /> : <FiMenu />}
      </MenuToggle>

      <NavLinks $open={menuOpen}>
        {NAV_LINKS.map((link) =>
          link.dropdown ? (
            <DropdownWrapper key={link.label} ref={dropdownRef}>
              <DropdownTrigger
                as={NavLink}
                to={link.to}
                onClick={close}
                onMouseEnter={() => setDropdownOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                    e.preventDefault();
                    setDropdownOpen(true);
                  }
                  if (e.key === "Escape") setDropdownOpen(false);
                }}
                aria-haspopup="true"
                aria-expanded={dropdownOpen}
              >
                {link.label}
                <FiChevronDown
                  style={{
                    marginLeft: "0.2rem",
                    transition: "transform 0.2s",
                    transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </DropdownTrigger>
              <DropdownMenu
                $open={dropdownOpen}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                {link.dropdown.map((sub) => (
                  <DropdownItem key={sub.to} to={sub.to} onClick={close}>
                    {sub.label}
                  </DropdownItem>
                ))}
              </DropdownMenu>
              {/* Mobile: always-visible sub-link */}
              <MobileSubLinks>
                {link.dropdown.map((sub) => (
                  <MobileSubLink key={sub.to} to={sub.to} onClick={close}>
                    {sub.label}
                  </MobileSubLink>
                ))}
              </MobileSubLinks>
            </DropdownWrapper>
          ) : (
            <StyledLink key={link.label} to={link.to} onClick={close}>
              {link.label}
            </StyledLink>
          )
        )}

      </NavLinks>
    </NavContainer>
  );
};

export default Navbar;

// ---------------- Styled Components ----------------

const NavContainer = styled.nav`
  width: 100%;
  padding: 0.875rem 2rem;
  background-color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 0 #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 999;
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Avatar = styled.img`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  object-fit: cover;
`;

const Logo = styled(NavLink)`
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  text-decoration: none;
  letter-spacing: -0.01em;

  &:hover {
    color: #4f46e5;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: 768px) {
    position: absolute;
    top: 61px;
    left: 0;
    right: 0;
    background: #ffffff;
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem 1.5rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
    gap: 0;
    display: ${({ $open }) => ($open ? "flex" : "none")};
  }
`;

const linkBase = `
  font-size: 0.9375rem;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  padding: 0.4rem 0.65rem;
  border-radius: 0.375rem;
  transition: color 0.2s, background 0.2s;

  &.active {
    color: #4f46e5;
    font-weight: 600;
  }

  &:hover {
    color: #4f46e5;
    background: #f5f3ff;
  }

  @media (max-width: 768px) {
    padding: 0.6rem 0;
    border-radius: 0;
    width: 100%;
    border-bottom: 1px solid #f3f4f6;

    &:hover {
      background: none;
    }
  }
`;

const StyledLink = styled(NavLink)`
  ${linkBase}
`;

// ---- Dropdown ----

const DropdownWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DropdownTrigger = styled(NavLink)`
  ${linkBase}
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  min-width: 160px;
  padding: 0.375rem;
  display: ${({ $open }) => ($open ? "flex" : "none")};
  flex-direction: column;
  z-index: 1000;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DropdownItem = styled(NavLink)`
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  text-decoration: none;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: #f5f3ff;
    color: #4f46e5;
  }

  &.active {
    color: #4f46e5;
    font-weight: 600;
  }
`;

// Mobile-only: sub-links shown inline below the parent link
const MobileSubLinks = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

const MobileSubLink = styled(NavLink)`
  font-size: 0.875rem;
  font-weight: 400;
  color: #6b7280;
  text-decoration: none;
  padding: 0.5rem 0 0.5rem 1rem;
  border-bottom: 1px solid #f3f4f6;

  &:hover {
    color: #4f46e5;
  }

  &.active {
    color: #4f46e5;
  }
`;

// ---- Hamburger ----

const MenuToggle = styled.button`
  background: none;
  border: none;
  font-size: 1.4rem;
  color: #111827;
  display: none;
  padding: 0.5rem;
  cursor: pointer;
  line-height: 1;
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    display: flex;
  }

  &:hover {
    color: #4f46e5;
  }
`;
